import { renderShell, el, clear, appendAll } from './components/layout';
import { getAdminKey, setAdminKey, clearAdminKey, api, ApiError } from './api';
import { renderRunsPage, disposeRunsPage } from './pages/runs';
import { renderRunDetailPage, disposeRunDetailPage } from './pages/run-detail';
import { renderNewRunPage } from './pages/new-run';
import { renderClientsPage } from './pages/clients';
import { renderSubscriptionsPage } from './pages/subscriptions';

export type Route =
  | { name: 'runs' }
  | { name: 'new' }
  | { name: 'detail'; id: number }
  | { name: 'clients' }
  | { name: 'subscriptions' }
  | { name: 'login' };

export function parseHash(): Route {
  const raw = location.hash.replace(/^#/, '') || '/runs';
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  if (path === '/runs/new') return { name: 'new' };
  const detail = path.match(/^\/runs\/(\d+)$/);
  if (detail) return { name: 'detail', id: Number(detail[1]) };
  if (path.startsWith('/subscriptions')) return { name: 'subscriptions' };
  if (path.startsWith('/clients')) return { name: 'clients' };
  if (path.startsWith('/login')) return { name: 'login' };
  return { name: 'runs' };
}

function renderLogin(mount: HTMLElement) {
  clear(mount);
  const wrap = el('div', { className: 'login-wrap' });
  const card = el('div', { className: 'login-card' });
  appendAll(card, el('h1', { text: 'Prospector' }),
    el('p', { text: 'Enter the admin bypass key to continue.' }),
  );
  const input = el('input', {
    className: 'input',
    type: 'password',
    placeholder: 'Admin key',
    autocomplete: 'current-password',
  }) as HTMLInputElement;
  const err = el('div', { className: 'muted', style: 'margin:10px 0;min-height:18px' });
  const btn = el('button', {
    className: 'btn btn-primary',
    type: 'button',
    text: 'Sign in',
    style: 'width:100%;margin-top:8px',
  });

  async function submit() {
    const key = input.value.trim();
    if (!key) {
      err.textContent = 'Key required';
      return;
    }
    setAdminKey(key);
    try {
      await api('/api/health');
      await api('/api/runs');
      location.hash = '#/runs';
      route();
    } catch (e) {
      clearAdminKey();
      err.textContent =
        e instanceof ApiError ? e.message : 'Could not verify key';
    }
  }

  btn.addEventListener('click', submit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') submit();
  });

  appendAll(card, input, err, btn);
  wrap.appendChild(card);
  mount.appendChild(wrap);
}

async function pendingCount(): Promise<number> {
  try {
    const data = await api<{ count: number }>('/api/deliveries/pending-count');
    return Number(data.count) || 0;
  } catch {
    return 0;
  }
}

export async function route() {
  const mount = document.getElementById('app');
  if (!mount) return;

  disposeRunsPage();
  disposeRunDetailPage();

  const r = parseHash();
  if (!getAdminKey() && r.name !== 'login') {
    location.hash = '#/login';
    renderLogin(mount);
    return;
  }

  if (r.name === 'login') {
    renderLogin(mount);
    return;
  }

  const pending = await pendingCount();
  const pageRoot = el('div');
  if (r.name === 'runs') {
    renderShell(mount, 'runs', pageRoot, pending);
    renderRunsPage(pageRoot);
  } else if (r.name === 'new') {
    renderShell(mount, 'new', pageRoot, pending);
    renderNewRunPage(pageRoot);
  } else if (r.name === 'detail') {
    renderShell(mount, 'runs', pageRoot, pending);
    renderRunDetailPage(pageRoot, r.id);
  } else if (r.name === 'subscriptions') {
    renderShell(mount, 'subscriptions', pageRoot, pending);
    renderSubscriptionsPage(pageRoot);
  } else if (r.name === 'clients') {
    renderShell(mount, 'clients', pageRoot, pending);
    renderClientsPage(pageRoot);
  }
}
